import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/ScrollReveal";

const Projets = () => {
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("published", true)
        .order("event_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  return (
    <>
      <Helmet>
        <title>Projets et Événements - Agroref</title>
        <meta 
          name="description" 
          content="Découvrez nos projets réalisés et nos événements à venir." 
        />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section className="bg-[#2c2c2c] text-white py-12 md:py-16 md:py-20 relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 md:mb-12">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">Projets et Événements</h1>
              <div className="w-32 md:w-48 h-0.5 bg-accent mx-auto mb-6"></div>
              <p className="text-base md:text-lg text-white">
                Découvrez nos réalisations et événements
              </p>
            </div>
          </div>
          
          {/* Courbe blanche en bas */}
          <div className="absolute bottom-0 left-0 right-0 -mb-px">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
              <path d="M0 0C240 53.3333 480 80 720 80C960 80 1200 53.3333 1440 0V80H0V0Z" fill="white"/>
            </svg>
          </div>
        </section>

        {/* Projects Section */}
        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <h2 className="text-3xl font-bold mb-8 text-center">Nos Projets</h2>
            </ScrollReveal>
            {projectsLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Chargement des projets...</p>
              </div>
            ) : projects && projects.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {projects.map((project, index) => (
                  <ScrollReveal key={project.id} delay={index * 0.1}>
                    <Link to={`/projets/${project.slug}`}>
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                        {project.images && project.images[0] && (
                          <div className="aspect-video overflow-hidden rounded-t-lg">
                            <img 
                              src={project.images[0]}
                              alt={project.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <CardHeader>
                          <CardTitle className="line-clamp-2">{project.title}</CardTitle>
                        </CardHeader>
                        {project.description && (
                          <CardContent>
                            <p className="text-muted-foreground line-clamp-3">
                              {project.description}
                            </p>
                          </CardContent>
                        )}
                      </Card>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Aucun projet publié pour le moment.</p>
              </div>
            )}
          </div>
        </section>

        {/* Events Section */}
        <section className="bg-gray-50 py-12 md:py-16">
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <h2 className="text-3xl font-bold mb-8 text-center">Nos Événements</h2>
            </ScrollReveal>
            {eventsLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Chargement des événements...</p>
              </div>
            ) : events && events.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {events.map((event, index) => (
                  <ScrollReveal key={event.id} delay={index * 0.1}>
                    <Link to={`/evenements/${event.slug}`}>
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                        {event.image_url && (
                          <div className="aspect-video overflow-hidden rounded-t-lg">
                            <img 
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <CardHeader>
                          <CardTitle className="line-clamp-2">{event.title}</CardTitle>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                            <Calendar className="h-4 w-4" />
                            <time dateTime={event.event_date}>
                              {new Date(event.event_date).toLocaleDateString("fr-FR", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </time>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              <span>{event.location}</span>
                            </div>
                          )}
                        </CardHeader>
                        {event.description && (
                          <CardContent>
                            <p className="text-muted-foreground line-clamp-3">
                              {event.description}
                            </p>
                          </CardContent>
                        )}
                      </Card>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Aucun événement publié pour le moment.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Projets;
